import { lazy } from "react";
import type { ComponentType } from "react";

function importWithRetry<T>(
  factory: () => Promise<T>,
  retries = 3,
  interval = 1000
): Promise<T> {
  return factory().catch((error: unknown) => {
    if (retries <= 0) {
      throw error;
    }
    return new Promise<T>((resolve) =>
      setTimeout(
        () => resolve(importWithRetry(factory, retries - 1, interval * 2)),
        interval
      )
    );
  });
}

/**
 * Creates a lazy-loaded component from a named export with automatic retry.
 * Retries failed dynamic imports up to 3 times with exponential backoff.
 */
export const lazyNamed = <T extends Record<string, unknown>>(
  factory: () => Promise<T>,
  name: keyof T
) =>
  lazy(() =>
    importWithRetry(factory).then((m) => ({
      default: m[name] as ComponentType,
    }))
  );
